import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import DocumentTemplateUtils from 'services/DocumentTemplateUtils';
import { v4 as uuidV4 } from 'uuid';
import Icon from 'components/Utils/NewIcon';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import SelectedElementEditPanel, {
  MultipleSelectedElementsPanel,
} from 'components/HealthRecords/Documents/DocumentTemplate/SelectedElementEditPanel';
import Collapse, { UncontrolledCollapse } from 'components/Utils/Collapse';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { DefaultSwitch as Switch } from 'services/UIUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import DocumentUtils from 'services/DocumentUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import NewDocumentTemplate from 'components/HealthRecords/Documents/NewDocumentTemplate';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { EDITABLE_BY_ROLES } from '../SelectedElementEditPanel/EditableByRolesEnum';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';
import { StaffBadge } from 'services/StaffUtils';

const DocumentTemplateEditPanel = (props) => {
  const dispatch = useAppDispatch();

  const templateCategories = useAppSelector((state) => state.templates.templateCategories);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const [saving, setSaving] = useState(false);
  const [replacePdf, setReplacePdf] = useState(false);

  let {
    onClose,
    deleteTemplate,
    templateEdit,
    selectedEditElement,
    template,
    change,
    copiedStyle,
    updateField,
    setTemplate,
    selectEditElement,
    addMultipleNewElements,
    addElement,
    previousElements = [],
    setPreviousElements,
    pageWidth,
    offsetX,
    offsetY,
    numberPreviousFields,
    rowNumberFields,
    control,
  } = props;

  templateEdit = templateEdit || {};
  let {
    description,
    category_id,
    secondary_name,
    fax,
    clinic_id,
    hide_document,
    pinned,
    document_type,
    allow_patient_fill_multiple_times,
    allow_submission,
    change_date_when_edited,
  } = templateEdit;

  let className = 'edit-panel padded-box full-height';

  function copyStyle(element) {
    change('copiedStyle', {
      w_percent: element.w_percent,
      h_percent: element.h_percent,
      style: element.style,
      scale: element.scale,
      letter_spacing: element.letter_spacing,
      line_height: element.line_height,
      font_size: element.font_size,
      background: element.background,
      text_indent: element.text_indent,
      type: element.type,
      metadata: element.metadata,
      circle: element.circle,
      circle_scale: element.circle_scale,
    });
  }

  function onTemplateChange(event) {
    let templateEdit = {
      ...props.templateEdit,
      [event.target.name]: event.target.value,
    };
    change('templateEdit', templateEdit);
  }

  function pasteStyle(selectedEditElement) {
    updateField(selectedEditElement.id, { ...copiedStyle });
  }

  function revertPreviousElements() {
    let { numberPreviousFields } = props;
    let count = 0;

    let ids = [];

    for (
      let i = previousElements.length - 1;
      i >= 0 && count < numberPreviousFields;
      i--, count++
    ) {
      ids.push(previousElements[i].element.id);
    }
    removeFields(ids);
  }

  function getPageHeight(pageNumber) {
    let page = templateEdit.fields[pageNumber];
    let { height, width } = page;
    return (height * pageWidth) / width;
  }

  function applyPreviousElements() {
    offsetX = parseInt(offsetX);
    offsetY = parseInt(offsetY);

    let percentX = DocumentTemplateUtils.percent(offsetX, pageWidth);

    numberPreviousFields = parseInt(numberPreviousFields);
    let count = 0;

    let allPrevious = PerformanceUtils.clone(previousElements);

    let elements = [];
    let pageNumber;

    let fields = [];

    for (let i = allPrevious.length - 1; i >= 0 && count < numberPreviousFields; i--, count++) {
      if (allPrevious[i]) fields.push(allPrevious[i]);
    }

    _.reverse(fields);

    fields.forEach((field) => {
      let previousElement = field.element;
      if (typeof previousElement === 'object') {
        pageNumber = field.pageNumber;

        let pageHeight = getPageHeight(pageNumber);
        let percentY = DocumentTemplateUtils.percent(offsetY, pageHeight);

        let element = templateEdit.fields[pageNumber].inputs.find(
          (element) => element.id === previousElement.id,
        );
        element = PerformanceUtils.clone(element);

        if (element) {
          element.id = uuidV4();
          let x = DocumentTemplateUtils.addPercentages(element.x_percent, percentX);
          let y = DocumentTemplateUtils.addPercentages(element.y_percent, percentY);

          x = DocumentTemplateUtils.percentToPixel(x, pageWidth);
          y = DocumentTemplateUtils.percentToPixel(y, pageHeight);

          elements.push({ ...element, x, y });
        }
      }
    });
    addMultipleNewElements(pageNumber, elements);
  }

  function applyRowElements() {
    rowNumberFields = parseInt(rowNumberFields);

    let allPrevious = PerformanceUtils.clone(previousElements);
    let previous = allPrevious[allPrevious.length - 1];

    let prevX = previous.element.x;
    let wPixels = DocumentTemplateUtils.percentToPixel(previous.element.w_percent, pageWidth);

    let elements = [];
    let pageNumber;

    for (let i = 0; i < rowNumberFields; i++) {
      let previousElement = previous.element;
      if (typeof previousElement === 'object') {
        pageNumber = previous.pageNumber;

        let pageHeight = getPageHeight(pageNumber);
        let percentY = DocumentTemplateUtils.percent(0, pageHeight);

        let element = templateEdit.fields[pageNumber].inputs.find(
          (element) => element.id === previousElement.id,
        );
        element = PerformanceUtils.clone(element);

        if (element) {
          element.id = uuidV4();
          let newX = prevX + (wPixels - 0) * i;

          let y = DocumentTemplateUtils.addPercentages(element.y_percent, percentY);
          y = DocumentTemplateUtils.percentToPixel(y, pageHeight);

          elements.push({ ...element, x: newX, y });
        }
      }
    }
    addMultipleNewElements(pageNumber, elements);
  }

  function removeFields(ids) {
    let myEdit = PerformanceUtils.clone(templateEdit);
    myEdit.fields = myEdit.fields.map((page) => {
      return {
        ...page,
        inputs: page.inputs.filter((item) => !ids.includes(item.id)),
      };
    });
    setPreviousElements(previousElements.filter((item) => !ids.includes(item.element.id)));
    change('templateEdit', myEdit);
  }

  function removeField(id) {
    removeFields([id]);
  }

  function snapFields() {
    const myEdit = PerformanceUtils.clone(templateEdit);

    myEdit.fields.forEach((page, index) => {
      snapPixelsY(page.inputs, index);
    });

    change('templateEdit', myEdit);
  }

  function updateAllFields(edit) {
    const updatedFields = templateEdit.fields.map((page) => {
      const inputs = page.inputs.map((field) => {
        if (typeof field === 'object' && !!field.id) {
          return { ...field, ...edit };
        }
        return field;
      });
      return { ...page, inputs };
    });
    const update = { ...templateEdit, fields: updatedFields };
    change('templateEdit', update);
  }

  /**
   * Persist the current templateEdit to the server without leaving edit mode.
   * Returns the saved template (fields parsed) or undefined on failure.
   * @param {Partial<import('generated/api').DocumentTemplate>} edits
   */
  async function persistTemplateEdit(edits = {}, dispatchOptions = {}) {
    let myEdit = PerformanceUtils.clone(templateEdit);

    let data = {
      ...myEdit,
      fields: JSON.stringify(myEdit.fields),
      ...edits,
    };

    setSaving(true);
    const result = await dispatch(
      api.Templates.updateDocumentTemplate({
        id: myEdit.id,
        documentTemplate: data,
      }),
      { errorTitle: 'Document Template', ...dispatchOptions },
    );
    setSaving(false);

    if (!result) return undefined;
    const saved = {
      ...result,
      fields: JSON.parse(result.fields),
    };
    setTemplate(saved);
    return saved;
  }

  /** @param {Partial<import('generated/api').DocumentTemplate>} edits */
  async function editTemplate(edits = {}) {
    const saved = await persistTemplateEdit(edits);
    if (saved) {
      change('editMode', false);
      change('templateEdit', PerformanceUtils.clone(saved));
      change('selectedEditElement', null);
    }
  }

  // Replacing the PDF ends with the editor adopting server state, so any
  // unsaved layout changes are committed first — nothing gets thrown away.
  async function openReplacePdf() {
    const hasUnsavedChanges = JSON.stringify(templateEdit) !== JSON.stringify(template);
    if (hasUnsavedChanges) {
      const saved = await persistTemplateEdit({}, { successText: 'Template changes saved' });
      if (!saved) return; // save failed — don't proceed into the replace flow
      change('templateEdit', PerformanceUtils.clone(saved));
    }
    setReplacePdf(true);
  }

  function toggleAddElement(type) {
    type = type || false;
    change('addElement', type);
  }

  // After Replace PDF, the upload endpoint only returns a lite template, so
  // refetch the full one and refresh both the saved copy and the edit state —
  // otherwise the editor keeps showing the old pages until closed and reopened.
  async function onPdfReplaced() {
    const result = await dispatch(api.Templates.getDocumentTemplate({ id: templateEdit.id }), {
      errorTitle: 'Document Template',
    });
    if (!result) return;
    const refreshed = {
      ...result,
      fields: typeof result.fields === 'string' ? JSON.parse(result.fields) : result.fields,
    };
    setTemplate(refreshed);
    change('templateEdit', PerformanceUtils.clone(refreshed));
    change('selectedEditElement', null);
  }

  function getEditElement(id) {
    let data = templateEdit.fields;
    let found = false;
    let obj = undefined;
    data.forEach((page) => {
      if (!found) {
        page.inputs.forEach((item) => {
          if (item.id === id) {
            found = true;
            obj = item;
          }
        });
      }
    });
    return obj;
  }

  let requester;

  if (template?.requester_id && template?.is_pending) {
    requester = physicians[template.requester_id] || admins[template.requester_id];
  }

  // Fields still carrying AI-detection markers put the panel in review mode:
  // the review section is pinned on top and the regular settings are collapsed
  // out of the way until the reviewer approves (or chooses to keep editing).
  const reviewMode = (templateEdit.fields || []).some((page) =>
    (page.inputs || []).some((input) => !!input?.source),
  );

  // While fields are selected the panel is about that selection — tool
  // sections that don't act on it render as inert collapsed rows instead.
  const hasSelection = !!selectedEditElement || props.fieldsInSelection?.size > 0;

  const templateSettings = (
    <>
      {template?.is_pending && template?.instructions && (
        <>
          <span className="small-title">Instructions to create</span>
          <div className="instructions-box">{template.instructions}</div>
        </>
      )}
      <div className="absolute-icons">
        {template?.long_description && (
          <Icon src="circle-info" alt={template?.long_description} showTooltip />
        )}
        <ConfirmationIcon
          src="trash"
          confirmText="This will delete the template. Proceed?"
          onClick={deleteTemplate}
        />
      </div>
      <DefaultTextField
        name="description"
        label="Description"
        value={description || ''}
        onChange={onTemplateChange}
      />
      <DefaultSelectField
        key="category_id"
        name="category_id"
        label="Category"
        style={{ width: '100%' }}
        value={category_id || ''}
        onChange={onTemplateChange}
        inputElements={templateCategories.map((category) => (
          <MenuItem value={category.id} key={'select-' + category.id}>
            {category.name}
          </MenuItem>
        ))}
      />
      <DefaultTextField
        name="secondary_name"
        label="Sub-category"
        value={secondary_name || ''}
        onChange={onTemplateChange}
      />
      <DefaultSelectField
        key="document_type"
        name="document_type"
        label="Document Type"
        style={{ width: '100%' }}
        value={document_type || ''}
        onChange={onTemplateChange}
        inputs={PerformanceUtils.objectToArray(DocumentUtils.DOCUMENT_TYPE)}
      />
      <DefaultSelectField
        key="clinic_id"
        name="clinic_id"
        label="Clinic"
        style={{ width: '100%' }}
        value={clinic_id || 'all'}
        onChange={onTemplateChange}
        inputElements={[
          <MenuItem value={'all'} key={'select-all'}>
            All
          </MenuItem>,
          ...(clinics || []).map((clinic) => (
            <MenuItem value={clinic.id} key={'select-' + clinic.id}>
              {clinic.name}
            </MenuItem>
          )),
        ]}
      />
      <DefaultTextField
        name="fax"
        label="Fax Number"
        value={fax || ''}
        onChange={onTemplateChange}
      />
      {requester && (
        <>
          <span className="small-title">Requester</span>
          <StaffBadge user={requester} />
        </>
      )}
      <Switch
        reversed={true}
        name="hide_document"
        label="Hide Document From Patient"
        checked={hide_document}
        onChange={(event, isInputChecked) => {
          let myEvent = {
            target: { name: 'hide_document', value: isInputChecked },
          };
          onTemplateChange(myEvent);
        }}
      />
      <Switch
        reversed={true}
        name="pinned"
        label="Pin to top of CPP"
        checked={pinned}
        onChange={(event, isInputChecked) => {
          let myEvent = {
            target: { name: 'pinned', value: isInputChecked },
          };
          onTemplateChange(myEvent);
        }}
      />
      <Switch
        reversed={true}
        name="allow_patient_fill_multiple_times"
        label="Allow Patient to Fill Multiple Times"
        checked={allow_patient_fill_multiple_times}
        onChange={(event, isInputChecked) => {
          let myEvent = {
            target: {
              name: 'allow_patient_fill_multiple_times',
              value: isInputChecked,
            },
          };
          onTemplateChange(myEvent);
        }}
      />
      <Switch
        reversed={true}
        name="change_date_when_edited"
        label="Change Document Date When Edited"
        checked={change_date_when_edited}
        onChange={(event, isInputChecked) => {
          let myEvent = {
            target: {
              name: 'change_date_when_edited',
              value: isInputChecked,
            },
          };
          onTemplateChange(myEvent);
        }}
      />
      <Switch
        reversed={true}
        name="allow_submission"
        label="Show in Referral Submission Form"
        checked={allow_submission}
        onChange={(event, isInputChecked) => {
          let myEvent = {
            target: { name: 'allow_submission', value: isInputChecked },
          };
          onTemplateChange(myEvent);
        }}
      />
      <div className="my-2">
        <DefaultButton
          className="white full-width"
          name="Replace PDF"
          loading={saving}
          icon="file-pdf"
          onClick={openReplacePdf}
        />
      </div>
    </>
  );

  return (
    <div className={className}>
      <div className="edit-content">
        {reviewMode && (
          <AiReviewSection
            templateEdit={templateEdit}
            change={change}
            editTemplate={editTemplate}
            persistTemplateEdit={persistTemplateEdit}
            saving={saving}
            fieldsInSelection={props.fieldsInSelection}
            setExternalSelection={props.setExternalSelection}
          />
        )}
        <div className="row edit-element">
          <UncontrolledCollapse
            title="Template Information"
            // A template without its identity set needs attention first — open
            // the box until description and category are both filled.
            initialValue={!!(description && category_id)}
            className="flex flex-col grow gap-2"
          >
            {templateSettings}
          </UncontrolledCollapse>
        </div>

        <ReplacePDFModal
          open={replacePdf}
          setOpen={setReplacePdf}
          template={templateEdit}
          onReplaced={onPdfReplaced}
        />
        <SelectedElementEditPanel
          selectedEditElement={selectedEditElement}
          updateField={updateField}
          copyStyle={copyStyle}
          pasteStyle={pasteStyle}
          removeField={removeField}
          getEditElement={getEditElement}
          selectEditElement={selectEditElement}
        />
        {props.fieldsInSelection?.size ? (
          <MultipleSelectedElementsPanel
            selectedElements={props.fieldsInSelection}
            getEditElement={getEditElement}
            onChange={props.change}
            templateEdit={templateEdit}
            clearSelection={props.clearSelection}
          />
        ) : null}
        <div className="row edit-element">
          <span className="small-title">Add Element</span>
          {DocumentTemplateUtils.getInputTypes().map((value) => (
            <Switch
              key={'add-' + value}
              label={'Add ' + value}
              checked={addElement === value}
              onChange={(event, isInputChecked) =>
                toggleAddElement(isInputChecked ? value : undefined)
              }
            />
          ))}
        </div>
        {/* Acts on the current selection (shows a placeholder picker above the
            selected field), so unlike the tool sections below it must stay
            interactive while fields are selected. */}
        <div className="row edit-element">
          <Switch
            label="Quick Placeholder"
            checked={props.toggleQuickPlaceholder === true}
            onChange={(event, isInputChecked) =>
              props.change('toggleQuickPlaceholder', isInputChecked)
            }
          />
        </div>
        {hasSelection ? (
          <InertSection title="Edit Tools" />
        ) : (
          <div className="row edit-element">
            <UncontrolledCollapse title="Edit Tools">
              <div className="small-title">Copy Fields</div>
              <DefaultTextField
                name="numberPreviousFields"
                control={control}
                label="# Fields"
                style={{ width: '30%', marginLeft: '3%', marginRight: '3%' }}
              />
              <DefaultTextField
                name="offsetX"
                control={control}
                label="Offset X"
                style={{ width: '30%', marginRight: '3%' }}
              />
              <DefaultTextField
                name="offsetY"
                control={control}
                label="Offset Y"
                style={{ width: '30%' }}
              />
              <div className="flex place-content-center mt-2">
                <DefaultButton
                  name="Apply"
                  icon="check"
                  style={{ width: '40%', marginRight: '20px' }}
                  onClick={applyPreviousElements}
                />
                <DefaultButton
                  name="Revert"
                  icon="xmark"
                  style={{ width: '40%' }}
                  onClick={revertPreviousElements}
                />
              </div>
              <hr className="my-4" />
              <div className="small-title">Create Row</div>
              <DefaultTextField
                name="rowNumberFields"
                control={control}
                label="# Fields"
                style={{ width: '30%', marginLeft: '3%', marginRight: '3%' }}
              />
              <div className="flex mt-2">
                <DefaultButton
                  name="Apply"
                  icon="check"
                  style={{ width: '40%', marginLeft: '20px' }}
                  onClick={applyRowElements}
                />
              </div>
            </UncontrolledCollapse>
          </div>
        )}
        {hasSelection ? (
          <InertSection title="Edit All Elements" />
        ) : (
          <div className="row edit-element">
            <UncontrolledCollapse title="Edit All Elements" className="flex flex-col grow gap-2">
              <DefaultButton
                className="white w-[80%] self-center"
                name="All Editable by Everyone"
                onClick={() => updateAllFields({ editable_by: EDITABLE_BY_ROLES.everyone })}
              />
              <DefaultButton
                className="white w-[80%] self-center mb-2"
                name="All Editable by Staff Only"
                onClick={() => updateAllFields({ editable_by: EDITABLE_BY_ROLES.staff })}
              />
              <DefaultButton
                className="white w-[80%] self-center mb-2"
                name="Snap Elements"
                onClick={snapFields}
              />
            </UncontrolledCollapse>
          </div>
        )}
        <div className="mt-2 pb-3 flex flex-wrap gap-3">
          <DefaultButton
            name="Cancel"
            className="red"
            icon="xmark"
            style={{ width: '100px' }}
            disabled={saving || !template}
            onClick={onClose}
          />
          <DefaultButton
            name="Save"
            className="blue"
            icon="floppy-disk"
            style={{ width: '100px' }}
            disabled={template === undefined}
            onClick={() => editTemplate()}
            loading={saving}
          />
          {template?.is_pending && !reviewMode && (
            // While AI markers remain the review section's "Approve & Complete
            // Request" is the single completion path — it strips the markers so
            // the AI overlay clears. Don't offer a second complete-request here
            // that would finish the request while leaving the markers (and icon)
            // behind. Non-AI pending templates still complete from this button.
            <DefaultButton
              name="Save And Complete Request"
              className="blue"
              icon="floppy-disk"
              style={{ width: '100%' }}
              disabled={template === undefined}
              onClick={() => editTemplate({ is_pending: false })}
              loading={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const AI_REVIEW_LOW_CONFIDENCE = 0.4;

/**
 * Review panel for AI-detected fields (fields carrying `source`/`confidence`
 * markers from the upload-time detection pipeline). Pinned to the top of the
 * edit panel while any marker remains. Deliberately model-agnostic wording —
 * reviewers triage by "needs attention" (low confidence, any source), never by
 * which model produced a field. Approving strips the markers; the save also
 * triggers description generation on the backend once geometry is
 * human-confirmed.
 */
function AiReviewSection({
  templateEdit,
  change,
  editTemplate,
  persistTemplateEdit,
  saving,
  fieldsInSelection,
  setExternalSelection,
}) {
  const [confirmFinish, setConfirmFinish] = useState(false);
  // Focus mode for the triage buttons: 'attention' | 'autofilled' | null.
  // While active, every field OUTSIDE the focused group is dimmed on the page
  // so the ones to review stand out.
  const [focus, setFocus] = useState(null);
  const pages = templateEdit?.fields || [];
  const aiFields = [];
  pages.forEach((page, pageIndex) => {
    (page.inputs || []).forEach((input) => {
      if (input?.source) aiFields.push({ ...input, pageIndex });
    });
  });

  const needsAttention = aiFields.filter(
    (f) => f.confidence !== undefined && f.confidence < AI_REVIEW_LOW_CONFIDENCE,
  );
  const autoFilled = aiFields.filter((f) => f.placeholder && f.placeholder !== 'empty');
  const selectedCount = fieldsInSelection?.size || 0;

  // A finish confirmation is only meaningful for the flagged set it was shown
  // for — drop it once that set changes.
  useEffect(() => {
    setConfirmFinish(false);
  }, [needsAttention.length]);

  // Apply the focus dim to the page: fields outside the focused group fade.
  useEffect(() => {
    const cls = 'review-focus-dimmed';
    const focusList =
      focus === 'attention' ? needsAttention : focus === 'autofilled' ? autoFilled : null;
    const focusIds = focusList && new Set(focusList.map((f) => f.id));
    // Both the fields themselves and their provenance-overlay boxes/badges
    // (separate layer, tagged with the same data-field-id).
    const nodes = [
      ...document.querySelectorAll('.template-page .template-field'),
      ...document.querySelectorAll('.template-page .ai-provenance-overlay [data-field-id]'),
    ];
    nodes.forEach((node) => {
      const id = node.getAttribute('data-field-id');
      node.classList.toggle(cls, !!focusIds && (!id || !focusIds.has(id)));
    });
    return () => nodes.forEach((node) => node.classList.remove(cls));
  }, [focus, templateEdit]);

  // A focus whose group emptied (all approved/deleted) would dim everything —
  // drop back to showing all fields.
  useEffect(() => {
    if (focus === 'attention' && needsAttention.length === 0) setFocus(null);
    if (focus === 'autofilled' && autoFilled.length === 0) setFocus(null);
  }, [focus, needsAttention.length, autoFilled.length]);

  if (aiFields.length === 0) return null;

  function toggleFocus(which, list) {
    if (focus === which) {
      setFocus(null);
      setExternalSelection?.(new Set());
    } else {
      setFocus(which);
      selectAll(list);
    }
  }

  function selectAll(list) {
    setExternalSelection?.(new Set(list.map((f) => f.id)));
  }

  function acceptFields(ids) {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    const myEdit = PerformanceUtils.clone(templateEdit);
    (myEdit.fields || []).forEach((page) => {
      (page.inputs || []).forEach((input) => {
        if (idSet.size === 0 || idSet.has(input.id)) {
          delete input.source;
          delete input.confidence;
        }
      });
    });
    change('templateEdit', myEdit);
    setExternalSelection?.(new Set());
    return myEdit;
  }

  // Approve everything and leave the editor (editTemplate saves + exits edit
  // mode). Soft-gated: finishing with unreviewed flagged fields takes a second,
  // explicit click.
  async function approveAndFinish() {
    if (needsAttention.length > 0 && !confirmFinish) {
      setConfirmFinish(true);
      return;
    }
    const myEdit = acceptFields(new Set());
    await editTemplate({
      fields: JSON.stringify(myEdit.fields),
      // Approving IS the review — clear the pipeline state explicitly instead
      // of echoing 'completed' back (which kept the template in the AI queue
      // until a backend job got around to clearing it).
      detection_status: null,
      detection_started: null,
      // Finishing a requested template is also its completion — one exit, not
      // a second approval in the pending list. Keep-editing leaves is_pending
      // alone; "Save And Complete Request" covers that path.
      ...(templateEdit.is_pending ? { is_pending: false } : {}),
    });
  }

  // Approve everything but stay in the editor for manual work (placeholders,
  // field types...). Stripping the markers drops the panel out of review mode,
  // which un-collapses the regular tooling.
  async function approveAndKeepEditing() {
    const myEdit = acceptFields(new Set());
    const saved = await persistTemplateEdit({
      fields: JSON.stringify(myEdit.fields),
      detection_status: null,
      detection_started: null,
    });
    if (saved) change('templateEdit', PerformanceUtils.clone(saved));
  }

  return (
    <div className="row edit-element">
      <div className="flex flex-col grow gap-2">
        <span className="small-title">Review AI Setup</span>
        <div style={{ fontSize: '12px', lineHeight: 1.6 }}>
          <div>
            AI set up <b>{aiFields.length}</b> fields on this form
            {needsAttention.length > 0 && (
              <>
                {' · '}
                <b style={{ color: '#a9741c' }}>{needsAttention.length}</b> need your attention
              </>
            )}
          </div>
          <div style={{ color: '#888' }}>
            To fix a field: click it to select · drag on the page to select several · arrow keys
            move it (hold Shift for bigger steps) · Delete removes it. Approve fields that look
            right; anything missed can be added with the tools below.
          </div>
        </div>
        <DefaultButton
          className="full-width"
          name={
            focus === 'attention'
              ? 'Show All Fields'
              : `Show Fields Needing Attention (${needsAttention.length})`
          }
          onClick={() => toggleFocus('attention', needsAttention)}
          disabled={focus !== 'attention' && needsAttention.length === 0}
          title={
            focus === 'attention'
              ? 'Restores the other fields on the page'
              : 'Dims everything except the fields that need your attention'
          }
        />
        <DefaultButton
          className="full-width"
          name={
            focus === 'autofilled'
              ? 'Show All Fields'
              : `Show Auto-Filled Fields (${autoFilled.length})`
          }
          onClick={() => toggleFocus('autofilled', autoFilled)}
          disabled={focus !== 'autofilled' && autoFilled.length === 0}
          title={
            focus === 'autofilled'
              ? 'Restores the other fields on the page'
              : 'Dims everything except the fields that will be filled automatically from the patient record (name, date of birth, health card…) so you can verify each one is bound to the right box'
          }
        />
        <DefaultButton
          className="full-width"
          name={`Approve Selected (${selectedCount})`}
          onClick={() => acceptFields(fieldsInSelection)}
          disabled={selectedCount === 0}
        />
        <hr className="my-2" />
        <DefaultButton
          className="blue full-width"
          name={
            confirmFinish
              ? `Approve ${needsAttention.length} Unchecked Field${
                  needsAttention.length === 1 ? '' : 's'
                } Anyway?`
              : templateEdit.is_pending
              ? 'Approve & Complete Request'
              : 'Approve & Finish'
          }
          onClick={approveAndFinish}
          loading={saving}
        />
        <DefaultButton
          className="white full-width"
          name="Approve & Edit Manually"
          onClick={approveAndKeepEditing}
          loading={saving}
          title="Approves the AI setup and keeps the editor open for manual changes — placeholders, field types, and more"
        />
      </div>
    </div>
  );
}

/**
 * Placeholder for a tool section that doesn't apply to the current selection:
 * looks like the section's collapsed row, half-faded and inert, with a hover
 * explaining why. Keeps the panel layout stable instead of sections vanishing.
 */
const InertSection = ({ title }) => (
  <DefaultTooltip
    placement="left"
    title="Not available while fields are selected — click away from the selection to use this"
  >
    <div className="row edit-element" style={{ opacity: 0.5 }}>
      <div style={{ pointerEvents: 'none' }}>
        <Collapse title={title} collapsed={true} />
      </div>
    </div>
  </DefaultTooltip>
);

const ReplacePDFModal = ({ open, setOpen, template, onReplaced }) =>
  open && (
    <DefaultModal
      className="fit-modal"
      key="new-document-template-modal"
      content={
        <NewDocumentTemplate
          template={template}
          onClose={(result) => {
            setOpen(false);
            if (result) onReplaced?.(result);
          }}
        />
      }
      isOpen={open}
      onRequestClose={() => setOpen(false)}
    />
  );

function snapPixelsY(inputs, pageIndex) {
  const PAGE_WIDTH = 826;
  const MIN_X_DIST = 20; // Don't snap fields in the same vertical column
  const MAX_X_DIST = undefined; // previous: 40
  const MAX_Y_DIST = 5; // Maximum Y distance (in pixels) to consider for snapping
  const toRealCoords = (n) => (parseFloat(n) * PAGE_WIDTH) / 100;

  // Store original coordinates
  const originalCoords = inputs.map((input) => ({
    id: input.id,
    x_percent: input.x_percent,
    y_percent: input.y_percent,
    w_percent: input.w_percent,
  }));

  console.log(`\nBEFORE SNAPPING (Page ${pageIndex + 1}):`);
  originalCoords.forEach((coord, i) => {
    const widthStr = coord.w_percent ? `, W: ${coord.w_percent}%` : '';
    console.log(
      `  ${i + 1}. ID: ${coord.id}, X: ${coord.x_percent}%, Y: ${coord.y_percent}%${widthStr}`,
    );
  });

  let snapCount = 0;
  const snappedFields = new Set(); // Track fields that have already been snapped (persists across passes)

  // Create array of indices sorted by X coordinate (left to right)
  const sortedIndices = inputs
    .map((input, idx) => ({ idx, x: parseFloat(input.x_percent) }))
    .sort((a, b) => a.x - b.x)
    .map((item) => item.idx);

  // Single pass from left to right
  console.log(`\n--- Snapping Pass (left to right) ---`);

  // Process fields from left to right
  for (const ix of sortedIndices) {
    // Skip if this field has already been processed
    if (snappedFields.has(ix)) continue;

    const input = inputs[ix];
    const inputOriginal = originalCoords[ix];

    // Find snappable neighbors (only include neighbors that pass validation)
    const snappableNeighbors = [];

    for (let ix2 = 0; ix2 < inputs.length; ix2++) {
      if (ix === ix2) continue; // Skip self

      const otherOriginal = originalCoords[ix2];
      // Calculate minimum distance considering field widths
      const { minDistance: distance } = calculateMinDistance(
        inputOriginal,
        otherOriginal,
        toRealCoords,
      );

      // Check if this neighbor is snappable
      const validation = canSnapToNeighbor(
        ix,
        ix2,
        inputs,
        originalCoords,
        toRealCoords,
        MIN_X_DIST,
        MAX_X_DIST,
        MAX_Y_DIST,
      );

      // Only add to list if it's a valid snap candidate
      if (validation.canSnap) {
        snappableNeighbors.push({ idx: ix2, distance });
      }
    }

    // Separate neighbors into two groups: already snapped vs not snapped
    const snappedNeighbors = snappableNeighbors
      .filter((n) => snappedFields.has(n.idx))
      .sort((a, b) => a.distance - b.distance);

    const unsnappedNeighbors = snappableNeighbors
      .filter((n) => !snappedFields.has(n.idx))
      .sort((a, b) => a.distance - b.distance);

    // Prioritize already-snapped neighbors (they're aligned), then unsnapped ones
    const closestNeighbors = [...snappedNeighbors, ...unsnappedNeighbors].slice(0, 5);

    if (closestNeighbors.length === 0) continue; // No snappable neighbors found

    // Snap to the closest valid neighbor
    if (closestNeighbors.length > 0) {
      const neighbor = closestNeighbors[0];
      const nearestIdx = neighbor.idx;
      const minDistance = neighbor.distance;
      const otherInput = inputs[nearestIdx];
      const otherOriginal = originalCoords[nearestIdx];

      // Calculate distances for logging
      const { distX, distY } = calculateMinDistance(inputOriginal, otherOriginal, toRealCoords);

      // Perform the snap
      const oldY = input.y_percent;
      const alreadyAligned = oldY === otherInput.y_percent;
      input.y_percent = otherInput.y_percent;
      const wasAlreadySnapped = snappedFields.has(nearestIdx);
      const neighborType = wasAlreadySnapped ? '[ALIGNED]' : '[NEW]';

      if (!alreadyAligned) {
        console.log(`\n  ✓ SNAPPED: ${input.id} → ${neighborType} ${otherOriginal.id}`);
        console.log(`    Y: ${oldY}% → ${input.y_percent}%`);
        console.log(
          `    (Euclidean dist: ${minDistance.toFixed(2)}px, distY: ${Math.abs(distY).toFixed(
            2,
          )}px, distX: ${distX.toFixed(2)}px)`,
        );
        console.log(
          `    (${closestNeighbors.length} snappable: ${snappedNeighbors.length} aligned, ${unsnappedNeighbors.length} new)`,
        );
        snapCount++;
      }

      // Mark the field that moved/joined as locked
      snappedFields.add(ix);

      // DON'T lock the target yet - let it have a chance to snap to something better
    }
  }

  console.log(`\n=== Snapping complete ===`);

  console.log(`\nAFTER SNAPPING (Page ${pageIndex + 1}):`);
  inputs.forEach((input, i) => {
    const original = originalCoords[i];
    const changed = original.y_percent !== input.y_percent ? ' ← CHANGED' : '';
    const widthStr = input.w_percent ? `, W: ${input.w_percent}%` : '';
    console.log(
      `  ${i + 1}. ID: ${input.id}, X: ${input.x_percent}%, Y: ${
        input.y_percent
      }%${widthStr}${changed}`,
    );
  });
  console.log(`\nTotal snaps on page ${pageIndex + 1}: ${snapCount}`);

  return originalCoords;
}

function canSnapToNeighbor(
  inputIndex,
  neighborIndex,
  inputs,
  originalCoords,
  toRealCoords,
  MIN_X_DIST,
  MAX_X_DIST,
  MAX_Y_DIST,
) {
  const input = inputs[inputIndex];
  const inputOriginal = originalCoords[inputIndex];
  const otherInput = inputs[neighborIndex];
  const otherOriginal = originalCoords[neighborIndex];

  // Use ORIGINAL coordinates for distance check - only snap fields that were originally close
  // Calculate minimum distance considering field widths
  const { distX, distY: distYSigned } = calculateMinDistance(
    inputOriginal,
    otherOriginal,
    toRealCoords,
  );
  const distY = Math.abs(distYSigned);

  // Check if fields are close enough in Y and far enough apart in X
  if (Math.abs(distY) >= MAX_Y_DIST) {
    return { canSnap: false, reason: 'Y distance too large' };
  }

  // Check if fields are in the same vertical column (overlapping in X)
  // Only apply MIN_X_DIST check if they actually overlap horizontally
  const overlapsHorizontally = fieldsOverlapHorizontally(
    inputOriginal,
    otherOriginal,
    toRealCoords,
  );

  if (overlapsHorizontally && distX <= MIN_X_DIST) {
    return { canSnap: false, reason: 'X distance too small (same column)' };
  }

  if (MAX_X_DIST && distX > MAX_X_DIST) {
    return { canSnap: false, reason: 'X distance too large (far away)' };
  }

  // Allow "snapping" to fields already at the same Y position
  // This is used to lock aligned fields together
  const targetY = otherInput.y_percent;
  const alreadyAligned = input.y_percent === targetY;

  // Check for overlap - would snapping create a collision with another field?
  const inputX = toRealCoords(inputOriginal.x_percent);
  // Check if snapping would cause this field to overlap with another field in the same column
  // Only block if there's a field in the SAME VERTICAL COLUMN at the target Y
  const wouldOverlap = inputs.some((field, fieldIdx) => {
    if (fieldIdx === inputIndex) return false; // Don't compare with self
    if (fieldIdx === neighborIndex) return false; // Don't compare with the snap target itself

    const fieldY = toRealCoords(field.y_percent);
    const targetYReal = toRealCoords(targetY);
    const fieldDistY = Math.abs(fieldY - targetYReal);

    // Only consider it an overlap if:
    // 1. The field is already at the target Y position (within 0.1px)
    // 2. The field is in the same vertical column (X positions overlap)
    if (fieldDistY < 0.1) {
      const inSameColumn = fieldsOverlapHorizontally(
        inputOriginal,
        { x_percent: field.x_percent, w_percent: field.w_percent },
        toRealCoords,
      );
      return inSameColumn;
    }

    return false;
  });

  if (wouldOverlap) {
    return { canSnap: false, reason: 'Would overlap with another field' };
  }

  return { canSnap: true };
}

function calculateMinDistance(field1, field2, toRealCoords) {
  const x1 = toRealCoords(field1.x_percent);
  const y1 = toRealCoords(field1.y_percent);
  const x2 = toRealCoords(field2.x_percent);
  const y2 = toRealCoords(field2.y_percent);

  const distY = Math.abs(y1 - y2);

  // Calculate horizontal distance considering field widths
  // Checkboxes (no w_percent) are assumed to be 10x10px
  const MIN_FIELD_WIDTH = 10;
  let distX;

  const w1 = field1.w_percent ? toRealCoords(field1.w_percent) : MIN_FIELD_WIDTH;
  const w2 = field2.w_percent ? toRealCoords(field2.w_percent) : MIN_FIELD_WIDTH;
  const x1Right = x1 + w1;
  const x2Right = x2 + w2;

  // Calculate minimum horizontal distance between edges
  distX = Math.min(
    Math.abs(x1 - x2), // left to left
    Math.abs(x1 - x2Right), // left1 to right2
    Math.abs(x1Right - x2), // right1 to left2
    Math.abs(x1Right - x2Right), // right to right
  );

  const minDistance = Math.sqrt(distX * distX + distY * distY);

  return { minDistance, distX, distY };
}

function fieldsOverlapHorizontally(field1, field2, toRealCoords) {
  const X_POSITION_THRESHOLD = 5; // Pixels - X positions closer than this are considered same column

  const x1 = toRealCoords(field1.x_percent);
  const x2 = toRealCoords(field2.x_percent);

  // Fields are in the same column if their X positions are very close
  // We don't care about widths here - just the starting positions
  return Math.abs(x1 - x2) < X_POSITION_THRESHOLD;
}

export default DocumentTemplateEditPanel;
